import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Modal } from './ui/Modal';
import { verifyMasterPassword } from '../utils/vaultFolderDelete';

/**
 * Confirming a folder deletion.
 *
 * THE DEFAULT IS SAFE AND THE DANGEROUS THING IS OPT-IN. The checkbox starts
 * cleared, so the plain path — the one a user reaches by pressing Enter without
 * reading — removes the folder and keeps every credential in it. Deleting the
 * contents requires ticking a box AND typing the master password.
 *
 * WHY THE PASSWORD, stated honestly because it differs by scope:
 *
 *   - PERSONAL items are protected by the master password, so this is a real
 *     authorization step: it proves the person at the keyboard is the owner and
 *     not someone who wandered up to an unlocked session.
 *   - TEAM items are NOT protected by it — the server authorizes those, and it
 *     will happily accept the delete from anyone holding the grant. Here the
 *     prompt is a PRESENCE CHECK, not an authorization one. It is worth having
 *     for that alone (an unattended session is the realistic way a team vault
 *     gets wrecked), and it is deliberately not dressed up as more than it is.
 *
 * The count is read from the plaintext index, so telling the user exactly how
 * many things they are about to destroy costs no decryption.
 */
export default function VaultFolderDeleteModal({ target, itemCount, onClose, onConfirm }) {
  const { t } = useTranslation();
  const [alsoItems, setAlsoItems] = useState(false);
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const secretRef = useRef(null);

  const open = !!target;
  const name = target?.folderName || target?.folder || '';
  const isTeam = target?.kind === 'teamFolder';

  // Reset per opening: a checkbox or a typed password surviving into the NEXT
  // folder's dialog is how the wrong thing gets deleted.
  useEffect(() => {
    if (!open) return;
    setAlsoItems(false);
    setSecret('');
    setError('');
    setBusy(false);
  }, [open, target?.folder, target?.folderId]);

  useEffect(() => {
    if (alsoItems) secretRef.current?.focus();
  }, [alsoItems]);

  if (!open) return null;

  const needsSecret = alsoItems && itemCount > 0;

  const submit = async () => {
    if (busy) return;
    setError('');

    if (needsSecret) {
      setBusy(true);
      const ok = await verifyMasterPassword(secret);
      if (!ok) {
        setBusy(false);
        setError(t('vault.folderDeleteWrongPassword'));
        secretRef.current?.select();
        return;
      }
    }

    setBusy(true);
    try {
      await onConfirm({ deleteItems: alsoItems && itemCount > 0 });
      onClose();
    } catch (err) {
      // Same rule as the folder CREATE path: a destructive action that fails
      // silently is indistinguishable from one that worked.
      // Tauri rejects a `Result<_, String>` with a PLAIN JS STRING, not an
      // Error — so `err.message` is undefined for every Rust-side failure and
      // the user got the generic fallback. That is what made the passkey
      // write-back refusal undiagnosable in the field.
      setError(
        (typeof err === 'string' ? err : err?.message) || t('vault.folderDeleteFailed')
      );
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={busy ? () => {} : onClose}
      title={t('vault.folderDeleteTitle', { name })}
      type="danger"
      customIcon={<Trash2 size={22} className="text-red-500" />}
      preventBackdropClose={busy}
      preventEscapeClose={busy}
    >
      <div className="space-y-3">
        <p className="text-sm text-gray-600 dark:text-zinc-300">
          {itemCount > 0
            ? t('vault.folderDeleteKeepsItems', { count: itemCount })
            : t('vault.folderDeleteEmpty')}
        </p>

        {itemCount > 0 && (
          <label className="flex items-start gap-2.5 text-sm text-gray-700 dark:text-zinc-200 cursor-pointer">
            <input
              type="checkbox"
              // The app's own checkbox, same as Teams / ShareWithTeamPanel /
              // WorkspaceModal. The native control ignores the accent colour and
              // looked foreign in a dialog this consequential.
              className="checkbox-brand mt-0.5"
              checked={alsoItems}
              disabled={busy}
              onChange={(e) => {
                setAlsoItems(e.target.checked);
                setError('');
              }}
            />
            <span>
              {t('vault.folderDeleteAlsoItems', { count: itemCount })}
              {/* `mt-1` because the hint ran straight into the label above it —
                  "…inside" and "Permanently destroys…" read as one sentence. */}
              <span className="mt-1 block text-xs leading-relaxed text-gray-500 dark:text-zinc-400">
                {t('vault.folderDeleteAlsoItemsHint')}
              </span>
            </span>
          </label>
        )}

        {needsSecret && (
          <div>
            <input
              ref={secretRef}
              type="password"
              className="modal-input w-full"
              placeholder={t('vault.folderDeletePasswordPlaceholder')}
              value={secret}
              disabled={busy}
              autoComplete="current-password"
              onChange={(e) => {
                setSecret(e.target.value);
                setError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
              }}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
              {isTeam
                ? t('vault.folderDeletePasswordWhyTeam')
                : t('vault.folderDeletePasswordWhyPersonal')}
            </p>
          </div>
        )}

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="flex justify-end space-x-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            // The password field being empty is what disables this, not a
            // separate "valid" flag — there is one source of truth for whether
            // the dangerous path is armed.
            disabled={busy || (needsSecret && !secret)}
            className="px-3 py-1.5 rounded-lg transition-colors modern-button text-sm bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
          >
            {busy
              ? t('vault.folderDeleteWorking')
              : alsoItems && itemCount > 0
                ? t('vault.folderDeleteConfirmAll', { count: itemCount })
                : t('vault.folderDeleteConfirm')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
